import type { ProcessOverviewVM } from '@/utils/workflowOverview'
import { MAP_GRID } from './mapTypes'

/** Where a step sits (spec §3.2): its lane, its index in the lane, its row
 * within the lane, its column, the global row across lanes, and the row's
 * direction (even rows read left to right, odd rows right to left). */
export interface Cell {
  id: string
  lane: string
  index: number
  row: number
  col: number
  globalRow: number
  dir: 1 | -1
  number: number
}
export interface LanePlacement { id: string; label: string; count: number; rows: number; firstGlobalRow: number }
export interface Placement {
  cells: Map<string, Cell>
  lanes: LanePlacement[]
  rowCount: number
  /** By global row, cards in column order. */
  rows: Cell[][]
  columns: number
}

function humanize(id: string): string {
  const s = id.replace(/[_-]+/g, ' ').trim()
  return s ? s[0].toUpperCase() + s.slice(1) : id
}

export function placeCells(vm: ProcessOverviewVM, columns: number): Placement {
  const cols = Math.max(MAP_GRID.MIN_COLUMNS, Math.floor(columns) || 0)
  const laneOrder = vm.lanes.map((l) => ({ id: l.id, label: l.label }))
  for (const s of vm.steps) {
    // D12: a lane the document does not declare becomes a trailing band.
    if (!laneOrder.some((l) => l.id === s.lane)) laneOrder.push({ id: s.lane, label: humanize(s.lane) })
  }
  const cells = new Map<string, Cell>()
  const lanes: LanePlacement[] = []
  const rows: Cell[][] = []
  let globalRow = 0
  for (const lane of laneOrder) {
    const steps = vm.steps.filter((s) => s.lane === lane.id)
    const rowCount = Math.max(1, Math.ceil(steps.length / cols))
    for (let r = 0; r < rowCount; r++) rows.push([])
    steps.forEach((s, i) => {
      const row = Math.floor(i / cols)
      const inRow = i % cols
      const dir: 1 | -1 = row % 2 === 0 ? 1 : -1
      const col = dir === 1 ? inRow : cols - 1 - inRow
      const cell: Cell = { id: s.id, lane: lane.id, index: i, row, col, globalRow: globalRow + row, dir, number: s.number }
      cells.set(s.id, cell)
      rows[globalRow + row].push(cell)
    })
    for (let r = 0; r < rowCount; r++) rows[globalRow + r].sort((a, b) => a.col - b.col)
    lanes.push({ id: lane.id, label: lane.label, count: steps.length, rows: rowCount, firstGlobalRow: globalRow })
    globalRow += rowCount
  }
  return { cells, lanes, rowCount: globalRow, rows, columns: cols }
}
